// Next.js compiles with SWC, not Babel, so core-js's usage-based polyfill injection (used for the
// webpack/Django bundle) doesn't apply here — structuredClone needs an explicit polyfill import.
// This can probably be removed at some point in the future
import 'core-js/actual/structured-clone'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useRouter } from 'next/router'
import NProgress from 'nprogress'
import { useEffect } from 'react'
import { NavigationGuardProvider } from 'next-navigation-guard'
import { ColorModeScript } from '~/design_system'
import { accountsKeys, useIsMFAVerifiedQuery } from '~/api/accounts'
import { useTermsAndConditionsAcceptedQuery } from '~/api/home'
import createDebugLogger from '~/common/logger'
import { NProgressInit } from '~/common/nextJsUtils'
import { queryClient } from '~/common/react-query/queryClient'
import { IntlProviderFromApi } from '~/components/IntlProviders'
import { PageErrorBoundary } from '~/components/v2/ErrorBoundary'
import { MainLayout } from '~/components/v2/layouts/MainLayout'
import { ModalProvider } from '~/hooks/useModal'
import { clearAllUrlState, pauseUrlStateChanges, resumeUrlStateChanges } from '~/hooks/useUrlState'
import GlobalCSSVariables from '~/theme/css/workdayTheme'
import { ThemedChakraProvider } from '~/theme/ThemedChakraProvider'
import { addMixpanelAnalyticsToPage, trackMixpanelPageView } from '~/third_party/analytics'
import '~/noneditable.css'
import './_globalStyles.css'
import { useAddInputModalityBodyAttribute } from '~/common/domUtils'
import '@workday/canvas-tokens-web/css/base/_variables.css'
import '@workday/canvas-tokens-web/css/system/_variables.css'
import '@workday/canvas-tokens-web/css/brand/_variables.css'

const useClearUrlState = () => {
  const router = useRouter()

  useEffect(() => {
    function beforeHistoryChange(nextUrl) {
      // Compare nextPath with currentPath to see if we are:
      // - navigating to a new page OR
      // - just refreshing the page
      //
      // If we are going to a new page (aka path) then clear the
      // useUrlState state so that we don't have query param bleed
      // from one page to the next.
      //
      // But if we are staying on the same page (just refreshing)
      // do not clear the query params, cause the user may have
      // filters, searching, ordering in place that they want to keep
      const currentPath = window.location.pathname
      const nextPath = nextUrl.split('?')[0]
      if (nextPath !== currentPath) {
        clearAllUrlState()
        pauseUrlStateChanges()
      }
    }

    function routeChangeComplete() {
      resumeUrlStateChanges()
    }

    router.events.on('beforeHistoryChange', beforeHistoryChange)
    router.events.on('routeChangeComplete', routeChangeComplete)

    return () => {
      router.events.off('beforeHistoryChange', beforeHistoryChange)
      router.events.off('routeChangeComplete', routeChangeComplete)
    }
  }, [router.events])
}

NProgressInit()

const usePageNavigationProgress = ({ trackPageViews = true }) => {
  const router = useRouter()

  useEffect(() => {
    function onStart() {
      NProgressInit()
      NProgress.start()
    }
    function onComplete() {
      NProgressInit()
      NProgress.done()
      if (trackPageViews) {
        trackMixpanelPageView()
      }
    }
    function onError() {
      NProgressInit()
      NProgress.done()
    }

    router.events.on('routeChangeStart', onStart)
    router.events.on('routeChangeComplete', onComplete)
    router.events.on('routeChangeError', onError)

    return () => {
      router.events.off('routeChangeStart', onStart)
      router.events.off('routeChangeComplete', onComplete)
      router.events.off('routeChangeError', onError)
    }
  }, [router.events])
}

export default function MyApp(props) {
  const { Component, pageProps } = props
  // If an SSR page has already fetched the accountsMe data, prepopulate the query cache
  if (pageProps.accountsMe) queryClient.setQueryData(accountsKeys.me(), pageProps.accountsMe)
  const trackPageViews = Component.trackPageViews !== false

  useClearUrlState()
  usePageNavigationProgress({ trackPageViews })

  useEffect(() => {
    if (trackPageViews) {
      addMixpanelAnalyticsToPage()
    }
  }, [])

  const Layout = Component.layout ?? MainLayout

  useEffect(() => {
    return function cleanupComponentChange() {
      resumeUrlStateChanges()
    }
  }, [useRouter().pathname])

  useAddInputModalityBodyAttribute()

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ThemedChakraProvider>
          <ColorModeScript initialColorMode="light" />
          <GlobalCSSVariables />
          <NavigationGuardProvider>
            <APIChecks Component={Component} />
            <IntlProviderFromApi anonymous={Component.anonymous} {...pageProps}>
              <ModalProvider>
                <Layout>
                  <PageErrorBoundary>
                    <Component {...pageProps} />
                  </PageErrorBoundary>
                </Layout>
                <StyleReactQueryDevtools />
              </ModalProvider>
            </IntlProviderFromApi>
          </NavigationGuardProvider>
        </ThemedChakraProvider>
      </QueryClientProvider>
    </>
  )
}

const APIChecks = ({ Component }) => {
  const router = useRouter()
  const enabled = router?.isReady && Component.anonymous !== true

  useTermsAndConditionsAcceptedQuery(router.asPath, { enabled })
  useIsMFAVerifiedQuery({ enabled })
  return null
}

const vitalsLoggerLog = createDebugLogger('WebVitals')

export function reportWebVitals(metric) {
  // These metrics can be sent to an analytics service in the future
  vitalsLoggerLog(metric)
}

function StyleReactQueryDevtools() {
  // The styles here place the toggle icon aligned with and
  // above the nextjs static indicator, and similar shadowing.
  return (
    <ReactQueryDevtools
      initialIsOpen={false}
      toggleButtonProps={{
        style: {
          width: '24px',
          opacity: 0.2
        }
      }}
    />
  )
}
